import { type MutableRefObject } from 'react';
import { type MenuInputOption } from '@saviynt/design-system';

import type { DataProvider, FormValues } from '../../../../shared/types';

import {
  areDependenciesSatisfied,
  type InitialDataForDependencies,
} from './dependencies';

// ============================================================================
// Cascading Utilities
// ============================================================================

// Handle cascading field clearing for SingleSelect
export const handleCascadingClear = (
  dataProvider: DataProvider,
  formValues: FormValues,
  relevantDependencyValues: string,
  prevDependencyValues: string,
  value: unknown,
  onChange?: (option: MenuInputOption) => void,
  initialData?: InitialDataForDependencies
): boolean => {
  const dependenciesSatisfied = areDependenciesSatisfied(
    dataProvider,
    formValues,
    initialData
  );
  const dependenciesChanged = prevDependencyValues !== relevantDependencyValues;

  // Clear value if dependencies are not satisfied
  if (!dependenciesSatisfied && value && onChange) {
    onChange({ label: '', value: '' });

    return true;
  }

  // Handle cascading when dependencies change
  if (
    dependenciesChanged &&
    dependenciesSatisfied &&
    dataProvider.dependencies &&
    Object.keys(dataProvider.dependencies).length > 0 &&
    prevDependencyValues !== ''
  ) {
    // Clear value if one exists
    if (value && onChange) {
      onChange({ label: '', value: '' });
    }

    // Return true to signal cascading occurred (for search text reset)
    // even if no value was cleared
    return true;
  }

  return false;
};

/**
 * Handles cascading field clearing for MultiSelect
 */
export const handleMultiSelectCascading = (
  dataProvider: DataProvider,
  formValues: FormValues,
  relevantDependencyValues: string,
  prevDependencyValues: string,
  value: unknown,
  isClearingRef: MutableRefObject<boolean>,
  onChange?: (selectedOptions: MenuInputOption[]) => void,
  initialData?: InitialDataForDependencies
): boolean => {
  if (isClearingRef.current) {
    return false;
  }

  const dependenciesSatisfied = areDependenciesSatisfied(
    dataProvider,
    formValues,
    initialData
  );
  const dependenciesChanged = prevDependencyValues !== relevantDependencyValues;

  if (!dependenciesChanged) {
    return false;
  }

  const isEmptyValue = !value || (Array.isArray(value) && value.length === 0);
  const hadPreviousDeps = prevDependencyValues !== '';

  // Clear value if dependencies are not satisfied and value exists
  if (!dependenciesSatisfied && !isEmptyValue && onChange) {
    // eslint-disable-next-line no-param-reassign
    isClearingRef.current = true;
    onChange([]);
    Promise.resolve()
      .then(() => {
        // eslint-disable-next-line no-param-reassign
        isClearingRef.current = false;
      })
      .catch(() => {
        // Ignore errors
      });

    return true;
  }

  // Handle cascading when dependencies change
  if (
    dependenciesSatisfied &&
    dataProvider.dependencies &&
    Object.keys(dataProvider.dependencies).length > 0 &&
    hadPreviousDeps
  ) {
    // Clear value if one exists
    if (!isEmptyValue && onChange) {
      // eslint-disable-next-line no-param-reassign
      isClearingRef.current = true;
      onChange([]);
      Promise.resolve()
        .then(() => {
          // eslint-disable-next-line no-param-reassign
          isClearingRef.current = false;
        })
        .catch(() => {
          // Ignore errors
        });
    }

    // Return true to signal cascading occurred (for search text reset)
    // even if no value was cleared
    return true;
  }

  return false;
};
